import React, { useState, memo } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Map common country names (from analytics) to ISO 3166-1 alpha-3 codes
const COUNTRY_NAME_TO_ISO = {
  'Deutschland': 'DEU', 'Germany': 'DEU',
  'Kamerun': 'CMR', 'Cameroon': 'CMR',
  'Nigeria': 'NGA',
  'Kongo': 'COD', 'Congo': 'COD', 'DR Kongo': 'COD',
  'Frankreich': 'FRA', 'France': 'FRA',
  'Schweiz': 'CHE', 'Switzerland': 'CHE',
  'Oesterreich': 'AUT', 'Austria': 'AUT',
  'USA': 'USA', 'United States': 'USA', 'Vereinigte Staaten': 'USA',
  'Grossbritannien': 'GBR', 'United Kingdom': 'GBR', 'UK': 'GBR',
  'Niederlande': 'NLD', 'Netherlands': 'NLD',
  'Belgien': 'BEL', 'Belgium': 'BEL',
  'Italien': 'ITA', 'Italy': 'ITA',
  'Spanien': 'ESP', 'Spain': 'ESP',
  'Polen': 'POL', 'Poland': 'POL',
  'Schweden': 'SWE', 'Sweden': 'SWE',
  'Norwegen': 'NOR', 'Norway': 'NOR',
  'Daenemark': 'DNK', 'Denmark': 'DNK',
  'Brasilien': 'BRA', 'Brazil': 'BRA',
  'Kanada': 'CAN', 'Canada': 'CAN',
  'Indien': 'IND', 'India': 'IND',
  'China': 'CHN',
  'Japan': 'JPN',
  'Suedafrika': 'ZAF', 'South Africa': 'ZAF',
  'Ghana': 'GHA',
  'Kenia': 'KEN', 'Kenya': 'KEN',
  'Tansania': 'TZA', 'Tanzania': 'TZA',
  'Elfenbeinkueste': 'CIV', 'Ivory Coast': 'CIV',
  'Togo': 'TGO',
  'Benin': 'BEN',
  'Senegal': 'SEN',
  'Ruanda': 'RWA', 'Rwanda': 'RWA',
  'Uganda': 'UGA',
  'Russland': 'RUS', 'Russia': 'RUS',
  'Tuerkei': 'TUR', 'Turkey': 'TUR',
  'Australien': 'AUS', 'Australia': 'AUS',
  'Unknown': null, 'Unbekannt': null,
};

const VisitorMap = memo(({ countries = [] }) => {
  const [tooltip, setTooltip] = useState(null);

  // Build lookup: ISO3 -> visitor data
  const countryData = {};
  let maxVisitors = 1;
  countries.forEach(c => {
    const iso = COUNTRY_NAME_TO_ISO[c.country] || COUNTRY_NAME_TO_ISO[c.country?.trim()];
    if (iso) {
      countryData[iso] = { views: c.views || 0, visitors: c.visitors || 0, name: c.country };
      if (c.visitors > maxVisitors) maxVisitors = c.visitors;
    }
  });

  const getColor = (iso3) => {
    const data = countryData[iso3];
    if (!data) return '#e2e8f0';
    const intensity = Math.min(data.visitors / maxVisitors, 1);
    // Gradient from light blue to deep blue
    const r = Math.round(219 - intensity * 160);
    const g = Math.round(234 - intensity * 140);
    const b = Math.round(254 - intensity * 50);
    return `rgb(${r},${g},${b})`;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="visitor-map">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          Besucher-Weltkarte
        </h3>
        {countries.length > 0 && (
          <span className="text-xs text-gray-500">{countries.length} Laender</span>
        )}
      </div>
      <div className="relative" style={{ height: 360 }}>
        {countries.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
            <p>Noch keine Laenderdaten vorhanden</p>
          </div>
        ) : (
          <>
            <ComposableMap
              projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }}
              style={{ width: '100%', height: '100%' }}
            >
              <ZoomableGroup center={[10, 30]} zoom={1}>
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const iso3 = geo.properties?.ISO_A3 || geo.id;
                      const data = countryData[iso3];
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={getColor(iso3)}
                          stroke="#cbd5e1"
                          strokeWidth={0.5}
                          style={{
                            hover: { fill: data ? '#2563eb' : '#f1f5f9', stroke: '#94a3b8', strokeWidth: 1, outline: 'none' },
                            pressed: { outline: 'none' },
                            default: { outline: 'none' },
                          }}
                          onMouseEnter={() => {
                            if (data) {
                              setTooltip({ name: data.name, visitors: data.visitors, views: data.views });
                            }
                          }}
                          onMouseLeave={() => setTooltip(null)}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
            {tooltip && (
              <div
                className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg px-4 py-3 pointer-events-none z-10"
                data-testid="map-tooltip"
              >
                <p className="font-bold text-gray-900 text-sm">{tooltip.name}</p>
                <p className="text-xs text-gray-500 mt-1">{tooltip.visitors} Besucher / {tooltip.views} Aufrufe</p>
              </div>
            )}
            {/* Legend */}
            <div className="absolute bottom-3 left-4 flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-2">
              <span className="text-[10px] text-gray-500">Weniger</span>
              <div className="flex h-3 rounded overflow-hidden">
                {[0.1, 0.3, 0.5, 0.7, 1].map((i) => (
                  <div key={i} style={{
                    width: 20,
                    backgroundColor: `rgb(${Math.round(219 - i * 160)},${Math.round(234 - i * 140)},${Math.round(254 - i * 50)})`
                  }} />
                ))}
              </div>
              <span className="text-[10px] text-gray-500">Mehr</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

VisitorMap.displayName = 'VisitorMap';

export default VisitorMap;
